import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, Bell, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Sidebar } from "@/components/portal/Sidebar";
import { PAGE_TITLES } from "@/components/portal/portalConfig";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const titleFor = (pathname) => PAGE_TITLES[pathname] || "Z2Infinity";

export const PortalShell = ({ config }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState("");

  const onSearch = (e) => {
    if (e.key === "Enter" && q.trim()) {
      navigate(`/student/practice/pyq?search=${encodeURIComponent(q.trim())}`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Fixed desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 lg:block" style={{ backgroundColor: "#0F172A" }}>
        <Sidebar config={config} />
      </aside>

      <div className="lg:pl-60">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-white/80 px-4 backdrop-blur-xl sm:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" aria-label="Open menu" className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary lg:hidden">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 border-0 p-0">
              <Sidebar config={config} onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <h1 className="font-heading text-lg font-bold tracking-tight text-foreground" data-testid="page-title">{titleFor(pathname)}</h1>

          <div className="ml-auto flex items-center gap-2">
            <div className="relative hidden sm:block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearch} data-testid="global-search" placeholder="Search questions…" className="h-9 w-48 rounded-full pl-9 lg:w-60" />
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="notif-bell" aria-label="Notifications" className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary">
                  <Bell className="h-5 w-5" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72">
                <p className="text-sm font-medium">Notifications</p>
                <p className="mt-2 text-sm text-muted-foreground">You're all caught up — no new alerts.</p>
              </PopoverContent>
            </Popover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="topbar-avatar" className="flex items-center gap-1.5 rounded-full pl-0.5 pr-2 hover:bg-secondary">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-xs font-bold uppercase text-white">
                    {user?.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" /> : (user?.name || "?")[0]}
                  </span>
                  <ChevronDown className="hidden h-4 w-4 text-muted-foreground sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.name}</div>
                  <div className="truncate text-xs font-normal text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/student/settings")} data-testid="menu-settings">Settings</DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-500">Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
